"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.pick = exports.isEqual = void 0;
const tslib_1 = require("tslib");
const createLogMessageBulk_1 = tslib_1.__importDefault(require("./api/createLogMessageBulk"));
const serializeException_1 = tslib_1.__importDefault(require("./utils/serializeException"));
const serializeMessage_1 = tslib_1.__importDefault(require("./utils/serializeMessage"));
const isEmpty = (value) => {
    return (value == null ||
        (typeof value === "object" && Object.keys(value).length === 0) ||
        (typeof value === "string" && value.trim().length === 0));
};
function isEqual(a, b) {
    if (a === b)
        return true;
    if (a == null || b == null || typeof a !== "object" || typeof b !== "object")
        return false;
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length)
        return false;
    for (const key of keysA) {
        if (!keysB.includes(key) || !isEqual(a[key], b[key]))
            return false;
    }
    return true;
}
exports.isEqual = isEqual;
function pick(object, keys) {
    return keys.reduce((obj, key) => {
        if (object && Object.prototype.hasOwnProperty.call(object, key)) {
            obj[key] = object[key];
        }
        return obj;
    }, {});
}
exports.pick = pick;
const LOG_LEVELS = {
    debug: 0,
    error: 4,
    info: 2,
    log: 1,
    warning: 3,
};
class LogClient {
    /**
     * Initializes the logging client
     * @param opts
     */
    static init(opts) {
        if (LogClient.instance &&
            !isEmpty(opts) &&
            !isEqual(opts, pick(LogClient.instance.getConfig(), Object.keys(opts)))) {
            throw new Error("LogClient configuration mismatch. Use getInstance().update(opts) to update configuration instead");
        }
        const client = LogClient.getInstance();
        if (opts) {
            client.update(opts);
        }
        // Start the timer for sending buffer to server
        if (!client.timer) {
            client.sendToServer();
        }
        return client;
    }
    /**
     * Retrieves the log client instance
     */
    static getInstance() {
        if (LogClient.instance) {
            return LogClient.instance;
        }
        LogClient.instance = new LogClient();
        return LogClient.instance;
    }
    constructor() {
        // Size of the buffer that should be met before posting data
        this.bufferSize = 200;
        // Enable or disable logging to the API (default: true)
        this.logToApi = true;
        // Enable or disable logging to console (default: true)
        this.logToConsole = true;
        // Message buffer
        this.buffer = [];
        // Intervals between send-to-server checks
        this.sendInterval = 30000;
        /**
         * Returns the configuration as an object
         */
        this.getConfig = () => {
            const { apiUrl, pageOrigin, bufferSize, logLevel, logToApi, logToConsole, version, sendInterval, sessionKey, shouldSendToServer, } = this;
            return {
                apiUrl,
                bufferSize,
                logLevel,
                logToApi,
                logToConsole,
                pageOrigin,
                sendInterval,
                sessionKey,
                shouldSendToServer,
                version,
            };
        };
        /**
         * Update configuration options on a running instance
         * @param opts
         */
        this.update = (opts) => {
            const { apiUrl, logLevel, pageOrigin, version, logToApi, logToConsole, bufferSize, sendInterval, shouldSendToServer, onSendFailure, sessionKey, } = opts;
            // Override default / existing config with new values
            const newConfig = {
                ...this.getConfig(),
                apiUrl,
                bufferSize,
                logLevel,
                logToApi,
                logToConsole,
                onSendFailure,
                pageOrigin,
                sendInterval,
                sessionKey,
                shouldSendToServer,
                version,
            };
            // Reset the timer with the new interval
            // if we receive a new logging interval,
            // otherwise if the logging interval is updated
            // it will wait for the timer to expire before
            // changing it
            if (this.getConfig().sendInterval !== newConfig.sendInterval || !this.timer) {
                if (this.timer) {
                    clearTimeout(this.timer);
                }
                this.timer = setTimeout(this.sendToServer, newConfig.sendInterval);
                this.nextSendTimestamp = new Date().getTime() + newConfig.sendInterval;
            }
            // Update configuration
            for (const key in newConfig) {
                if (newConfig[key] !== undefined) {
                    this[key] = newConfig[key];
                }
            }
            return this;
        };
        this.logError = (message, context = {}) => {
            const { requestID, ...restContext } = context;
            this.buffer.push(Promise.resolve({
                context: restContext,
                level: "error",
                originatingPage: this.pageOrigin,
                requestID,
                message,
                timestamp: new Date().getTime(),
                clientBrowser: navigator.userAgent,
            }));
            if (this.logToConsole) {
                console.error(message, context);
            }
        };
        this.logWarn = (message, context = {}) => {
            const { requestID, ...restContext } = context;
            this.buffer.push(Promise.resolve({
                context: restContext,
                level: "warning",
                originatingPage: this.pageOrigin,
                requestID,
                message,
                timestamp: new Date().getTime(),
                clientBrowser: navigator.userAgent,
            }));
            if (this.logToConsole) {
                console.warn(message, context);
            }
        };
        this.logInfo = (message, context = {}) => {
            const { requestID, ...restContext } = context;
            this.buffer.push(Promise.resolve({
                context: restContext,
                level: "info",
                originatingPage: this.pageOrigin,
                requestID,
                message,
                timestamp: new Date().getTime(),
                clientBrowser: navigator.userAgent,
            }));
            if (this.logToConsole) {
                console.info(message, context);
            }
        };
        this.logDebug = (message, context = {}) => {
            const { requestID, ...restContext } = context;
            this.buffer.push(Promise.resolve({
                context: restContext,
                level: "debug",
                originatingPage: this.pageOrigin,
                requestID,
                message,
                timestamp: new Date().getTime(),
                clientBrowser: navigator.userAgent,
            }));
            if (this.logToConsole) {
                console.debug(message, context);
            }
        };
        /**
         * @deprecated Use logError instead
         *
         * @param messages  Messages to log
         */
        this.error = (...messages) => {
            // Last option can be an options object:
            const optsOrMessage = messages[messages.length - 1];
            const requestID = optsOrMessage?.requestID || null;
            this.buffer.push(this.normalizeMessage("error", requestID, ...(requestID ? messages.slice(0, -1) : messages)));
            if (this.logToConsole) {
                console.error(...messages);
            }
            this.sendToServer();
        };
        /**
         * @deprecated Use logWarn instead
         *
         * @param messages  Messages to log
         */
        this.warn = (...messages) => {
            const optsOrMessage = messages[messages.length - 1];
            const requestID = optsOrMessage?.requestID || null;
            this.buffer.push(this.normalizeMessage("warning", requestID, ...(requestID ? messages.slice(0, -1) : messages)));
            if (this.logToConsole) {
                console.warn(...messages);
            }
        };
        /**
         * @deprecated Use logInfo instead
         *
         * @param messages  Messages to log
         */
        this.info = (...messages) => {
            const optsOrMessage = messages[messages.length - 1];
            const requestID = optsOrMessage?.requestID || null;
            this.buffer.push(this.normalizeMessage("info", requestID, ...(requestID ? messages.slice(0, -1) : messages)));
            if (this.logToConsole) {
                console.info(...messages);
            }
        };
        /**
         * @deprecated Use logInfo, logError, logWarn, logDebug instead
         *
         * @param messages  Messages to log
         */
        this.log = (...messages) => {
            const optsOrMessage = messages[messages.length - 1];
            const requestID = optsOrMessage?.requestID || null;
            this.buffer.push(this.normalizeMessage("log", requestID, ...(requestID ? messages.slice(0, -1) : messages)));
            if (this.logToConsole) {
                console.log(...messages);
            }
        };
        /**
         * @deprecated Use logDebug instead
         *
         * @param messages  Messages to log
         */
        this.debug = (...messages) => {
            const optsOrMessage = messages[messages.length - 1];
            const requestID = optsOrMessage?.requestID || null;
            this.buffer.push(this.normalizeMessage("debug", requestID, ...(requestID ? messages.slice(0, -1) : messages)));
            if (this.logToConsole) {
                console.debug(...messages);
            }
        };
        /**
         * Determines whether its time to flush the buffer and send
         * it to the API.
         * This can be overriden by options
         */
        this.shouldSend = async () => {
            // Never send to server if logToApi is false
            if (!this.logToApi) {
                return false;
            }
            const buffer = await Promise.all(this.buffer);
            if (this.shouldSendToServer) {
                return this.shouldSendToServer(buffer.filter((message) => LOG_LEVELS[message.level] >= LOG_LEVELS[this.logLevel]), this.lastSendTimestamp);
            }
            const msSinceSend = this.lastSendTimestamp ? new Date().getTime() - this.lastSendTimestamp : Infinity;
            // Always send if there's an error logged, dont wait
            const hasErrors = buffer.some(({ level }) => level === "error");
            // Send if the buffer is oversized
            const isBufferOversized = this.buffer.length >= this.bufferSize;
            // Also send if it's been too long since last send and we have messages.
            const hasMessages = buffer.length > 0;
            const isTimeoutExceeded = msSinceSend > this.sendInterval;
            if (!hasMessages) {
                return false;
            }
            // Always send if an error is logged, don't wait for the timer
            return hasErrors || isBufferOversized || isTimeoutExceeded;
        };
        this.sendToServer = async () => {
            // Note: private async methods
            // dont have access to "this",
            // unless they are bound as arrow functions
            const shouldSend = await this.shouldSend();
            // If this was called from a log call and not
            // from the timer, we might skip the timer,
            // flush the buffer and POST immediately, then
            // restart the timer.
            // Whenever an error is logged, this will be
            // invoked and we'll immediately flush the buffer
            // to avoid users having to wait on the error screen
            // until the next send
            if (this.timer) {
                clearTimeout(this.timer);
            }
            if (shouldSend) {
                const localBuffer = await Promise.all(this.flush());
                // Filter messages by log level
                const messagesToSend = localBuffer.filter((message) => LOG_LEVELS[message.level] >= LOG_LEVELS[this.logLevel]);
                if (messagesToSend.length > 0) {
                    await this.send(messagesToSend);
                }
                this.lastSendTimestamp = new Date().getTime();
            }
            this.timer = setTimeout(this.sendToServer, this.sendInterval);
            this.nextSendTimestamp = new Date().getTime() + this.sendInterval;
        };
        /**
         * Send messages to server
         */
        this.send = async (messages) => {
            const { json } = await (0, createLogMessageBulk_1.default)({ ignoreOnFailed: true, logEntries: messages }, { apiUrl: this.apiUrl, sessionKey: this.sessionKey });
            // Process errorInformationList into pairs of [Message, Error] and call onSendFailure with this list
            const { errorInformationList } = json?.data || { errorInformationList: null };
            if (errorInformationList && this.onSendFailure) {
                this.onSendFailure(errorInformationList.map(({ errorMessage, submissionIndex }) => [messages[submissionIndex], errorMessage]));
            }
            return json.data;
        };
        // Throw an error if instantiating with `new`, as this should
        // only be done once, and by the init method.
        if (LogClient.instance) {
            throw new Error("LogClient has already been instantiated! Use Logging.init() to retrieve the running instance.");
        }
    }
    async logException(error, context = {}) {
        const { requestID, ...restContext } = context;
        this.buffer.push(Promise.resolve({
            ...(await (0, serializeException_1.default)(error, restContext)),
            level: "error",
            originatingPage: this.pageOrigin,
            requestID,
        }));
        if (this.logToConsole) {
            console.error(error);
        }
    }
    /**
     * Flush the buffer and return a copy of the result
     */
    flush() {
        // FIRST flush the buffer by copying it and emptying it,
        // => to avoid race condition if the buffer was to be added to
        // while we're processing it
        const buffer = [...this.buffer];
        this.buffer = [];
        return buffer;
    }
    /**
     * Turns an array of any[] into objects the server can receive.
     * Objects, Arrays, and other non-serializable objects will be
     * extracted to context and assigned a variable name.
     *
     * A logging call like:
     * > log.info("Print", { test: "obj" }, "to console")
     *
     * will be serialized to:
     *
     * {
     *  level: "info",
     *  message: "Print { \"test\": \"obj\" } to console",
     *  context: {
     *      variable1: "{ \"test\": \"obj\" }"
     *   }
     * }
     */
    async normalizeMessage(level, requestID, ...messages) {
        const message = await (0, serializeMessage_1.default)(...messages);
        return {
            ...message,
            level,
            originatingPage: this.pageOrigin,
            requestID,
        };
    }
}
exports.default = LogClient;
